import { useContext } from 'react';
import { ReactEditor, useSlate } from 'slate-react';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { getBlockTagBindings, hasBlockTags } from 'app/slate/extensions/tag/types/block';
import { BlockTagChrome } from 'app/slate/extensions/tag/components/BlockTagChrome';
import { useBlockTagColors, textBlockTagRegionStyle } from 'app/slate/extensions/tag/utils/useBlockTagColors';

export const BlockTaggableList = ({ as: Tag, attributes, children, element, elementUUID }) => {
  const editor = useSlate();
  const { isTaggingMode, taggingSubMode } = useEditorMode();
  const { setBlockSelection, setClickedTagIds } = useContext(TagContext) ?? {};
  const { setIsSidebarOpen } = useContext(SidebarContext) ?? {};

  const bindings = getBlockTagBindings(element);
  const isBlockTagged = isTaggingMode && bindings.length > 0 && hasBlockTags(element);
  const blockTagColors = useBlockTagColors(bindings.length > 0 ? bindings : undefined);
  const showBlockChrome = isBlockTagged && blockTagColors && !blockTagColors.isHidden;
  const blockTagVisualStyle = showBlockChrome ? textBlockTagRegionStyle(blockTagColors) : null;

  const handleBlockClick = (e) => {
    if (!isTaggingMode || taggingSubMode === 'mark-coverage' || !elementUUID) return;
    if (e?.target?.closest?.('.ixbrl-tag')) return;
    if (bindings.length > 0) {
      setClickedTagIds?.(bindings.map((b) => b.tagId));
      setIsSidebarOpen?.(8);
      return;
    }
    try {
      const path = ReactEditor.findPath(editor, element);
      setBlockSelection?.({ kind: 'list', path, elementId: elementUUID });
    } catch (err) {
      console.error(`Element is not attached to the editor tree yet ${err}`);
    }
  };

  const list = (
    <Tag
      {...attributes}
      className={element.formatting?.class || undefined}
      data-element-uuid={elementUUID}
      onClick={isBlockTagged ? undefined : handleBlockClick}
    >
      {children}
    </Tag>
  );

  if (!isBlockTagged) return list;

  return (
    <div style={{ position: 'relative', ...(blockTagVisualStyle ?? {}) }} onClick={handleBlockClick}>
      {list}
      {showBlockChrome && <BlockTagChrome bindings={bindings} indicatorColor={blockTagColors.indicator} />}
    </div>
  );
};
